import { useMemo } from 'react';
import { useRawTheme } from '@balance-web/theme';

import type { ComboboxInputSize, ItemHeight } from './types';

type UseComboboxStateProps = {
  isItemDisabled: boolean;
  isFocused: boolean;
  isOpen: boolean;
  itemHeight?: ItemHeight;
  size: ComboboxInputSize;
};
/** Track common values for use in styled components. */
export function useComboboxState({
  isItemDisabled,
  isFocused,
  isOpen,
  itemHeight: consumerItemHeight,
  size,
}: UseComboboxStateProps) {
  const itemHeight = useItemHeight(consumerItemHeight, size);

  const state = useMemo(() => {
    return { isItemDisabled, isFocused, isOpen, itemHeight, size };
  }, [isItemDisabled, isFocused, isOpen, itemHeight, size]);

  return state;
}

const comboboxSizeToThemeKey = {
  xsmall: 'xsmall',
  small: 'small',
  medium: 'base',
} as const;

/** Default the item height, when none is provided by the consumer. */
export function useItemHeight(
  override: ItemHeight | undefined,
  size: ComboboxInputSize
) {
  const { borderWidth, sizing } = useRawTheme();
  // using the input height creates a nice relationship between the otherwise disconnected elements
  const inputHeight = sizing[comboboxSizeToThemeKey[size]];

  return override ?? inputHeight - borderWidth.standard * 2;
}
